import {
  BASIC_TYPE,
  IRoom,
  Scene,
  UserRole,
  ValidateRule,
  loggerManager,
  validate
} from 'trtc-js-sdk-core';
import { getValueType, isString, isUndefined } from 'trtc-js-sdk-core/src/utils/utils';
import TRTC from '../main';
import { EnterRoomConfig, LocalAudioConfig, LocalVideoConfig, RemoteVideoConfig, ScreenShareConfig, SwitchRoomConfig, TRTCStreamType } from '../type';
import RtcError from './rtc-error';
import { ErrorCode, ErrorCodeDictionary } from './error-code';
import { IS_SEI_SUPPORTED, isScreenCaptureApiAvailable, isSmallStreamSupported, isUsedInHttpProtocol } from 'trtc-js-sdk-core/src/common/rtc-detection';
import { convertVideoProfile } from '../utils';

const LocalVideoConfigOption = {
  type: BASIC_TYPE.Object,
  properties: {
    cameraId: { type: BASIC_TYPE.String },
    useFrontCamera: { type: BASIC_TYPE.Boolean },
    fillMode: { type: BASIC_TYPE.String, values: ['contain', 'cover', 'fill'] },
    mirror: { type: [BASIC_TYPE.String, BASIC_TYPE.Boolean], values: [true, false, 'view', 'publish', 'both'] },
    small: {
      type: [BASIC_TYPE.String, BASIC_TYPE.Object, BASIC_TYPE.Boolean],
      properties: {
        width: { type: BASIC_TYPE.Number },
        height: { type: BASIC_TYPE.Number },
        frameRate: { type: BASIC_TYPE.Number },
        bitrate: { type: BASIC_TYPE.Number }
      }
    },
    videoTrack: { instanceOf: MediaStreamTrack }
  }
};

const ScreenShareConfigOption = {
  type: BASIC_TYPE.Object,
  properties: {
    systemAudio: { type: BASIC_TYPE.Boolean },
    fillMode: { type: BASIC_TYPE.String, values: ['contain', 'cover', 'fill'] },
    profile: {
      type: [BASIC_TYPE.String, BASIC_TYPE.Object],
      properties: {
        width: { type: BASIC_TYPE.Number },
        height: { type: BASIC_TYPE.Number },
        frameRate: { type: BASIC_TYPE.Number },
        bitrate: { type: BASIC_TYPE.Number }
      }
    },
    // Electron 屏幕分享需传入自定义采集 track
    videoTrack: { instanceOf: MediaStreamTrack },
    audioTrack: { instanceOf: MediaStreamTrack }
  }
};

const ViewValidateConfig = {
  type: [BASIC_TYPE.String, HTMLElement, null, BASIC_TYPE.Array],
  arrayItem: {
    instanceOf: HTMLElement
  },
  validate(view: string | HTMLElement, key: string, fnName: string) {
    // 传入的 elementId 不在 DOM 对象中，报错
    if (isString(view)) {
      const container = document.getElementById(view as string);
      if (!container) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          extraCode: ErrorCodeDictionary.INVALID_ELEMENT_ID,
          fnName,
          messageParams: { key }
        });
      }
    }
  }
};

const UserIdValidateConfig = {
  name: 'userId',
  required: true,
  type: BASIC_TYPE.String
};

const LocalAudioConfigOption = {
  type: BASIC_TYPE.Object,
  properties: {
    microphoneId: { type: BASIC_TYPE.String },
    audioTrack: { instanceOf: MediaStreamTrack },
    captureVolume: { type: BASIC_TYPE.Number, min: 0 },
    earMonitorVolume: { type: BASIC_TYPE.Number, min: 0, max: 100 },
    profile: {
      type: [BASIC_TYPE.String, BASIC_TYPE.Object],
      properties: {
        bitrate: { type: BASIC_TYPE.Number },
        channelCount: { type: BASIC_TYPE.Number }
      }
    },
    echoCancellation: {
      values: [true, false, 'remote-only', 'all']
    },
    autoGainControl: { type: BASIC_TYPE.Boolean },
    noiseSuppression: { type: BASIC_TYPE.Boolean }
  }
};

function validateJoinState(isJoined: boolean, fnName: string) {
  if (!isJoined) {
    throw new RtcError({
      code: ErrorCode.INVALID_OPERATION,
      extraCode: ErrorCodeDictionary.INVALID_OPERATION_NOT_JOINED,
      fnName
    });
  }
}
function validateRemoteUserId(hasRemoteUserId: boolean, fnName: string, config: { userId: string; }) {
  if (!hasRemoteUserId) {
    throw new RtcError({
      code: ErrorCode.INVALID_OPERATION,
      extraCode: ErrorCodeDictionary.INVALID_OPERATION_REMOTE_USER_NOT_EXIST,
      fnName,
      messageParams: { value: config }
    });
  }
}
function validateRoomId(roomId: number, fnName: string, key: string) {
  // 且小于0xffffffff
  const isValidateInteger = /^[1-9]\d*$/.test(String(roomId)) && roomId < 0xffffffff;
  if (!isValidateInteger) {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_INTEGER,
      fnName,
      messageParams: { key }
    });
  }
}
function validateStrRoomId(strRoomId: string, fnName: string, key: string) {
  const isValidateString = /^[A-Za-z\d\s!#$%&()+\-:;<=.>?@[\]^_{}|~,]{1,64}$/.test(strRoomId);
  if (!isValidateString) {
    throw new RtcError({
      code: ErrorCode.INVALID_PARAMETER,
      extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_STRING,
      fnName,
      messageParams: { key }
    });
  }
}

// 用于实现编写参数校验配置时，可以使用 ts 动态提示功能
export type RuleConfig = { [key: string]: Readonly<ValidateRule | ValidateRule[]>; };
function createValidateConfig<T extends RuleConfig>(config: T) {
  return config;
}
const notLessThanZero = {
  type: BASIC_TYPE.Number,
  notLessThanZero: true
} as const;

export const isVideoProfileGreaterOrEqual = (profile1: VideoProfile, profile2: VideoProfile): boolean => profile1.width * profile1.height >= profile2.width * profile2.height && profile1.frameRate >= profile2.frameRate && profile1.bitrate >= profile2.bitrate;

function validateSmall(value: LocalVideoConfig) {
  if (!value?.option?.small) return;
  if (!isSmallStreamSupported()) {
    loggerManager.warn('small stream is not supported');
    delete value.option.small;
    return;
  }
  const bigProfile = convertVideoProfile(value.option.profile);
  const smallProfile = convertVideoProfile(value.option.small, true);
  if (!isVideoProfileGreaterOrEqual(bigProfile, smallProfile)) {
    loggerManager.warn(`small stream profile must be less than big stream profile. Big: ${JSON.stringify(bigProfile)}, Small: ${JSON.stringify(smallProfile)}`);
    delete value.option.small;
    return;
  }
}

const TRTCValidateConfig = createValidateConfig({
  create: [
    {
      name: 'RoomConfig',
      instanceOf: Function
    },
    {
      name: 'CreateConfig',
      type: BASIC_TYPE.Object,
      properties: {
        plugins: {
          type: BASIC_TYPE.Array,
          arrayItem: {
            instanceOf: Function
          }
        }
      }
    }],
  enterRoom: {
    name: 'EnterRoomConfig',
    type: BASIC_TYPE.Object,
    required: true,
    validate(this: { _room: IRoom; }, value: EnterRoomConfig, key: string, fnName: string) {
      if (this._room.isJoined) {
        throw new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          extraCode: ErrorCodeDictionary.INVALID_OPERATION_REPEAT_CALL,
          fnName
        });
      }
      if (value.roomId) {
        if (isString(value.roomId)) {
          throw new RtcError({
            code: ErrorCode.INVALID_PARAMETER,
            extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_INTEGER_STRING,
            fnName,
            messageParams: { key }
          });
        }
        validateRoomId(value.roomId, fnName, key);
      } else if (value.strRoomId) {
        validateStrRoomId(value.strRoomId, fnName, key);
      } else {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_REQUIRED,
          fnName
        });
      }
      if (value.quickStart) {
        if (value.scene && value.scene !== Scene.LIVE && value.role !== UserRole.AUDIENCE) {
          delete value.quickStart;
        }
      }
    },
    properties: {
      sdkAppId: {
        required: true,
        type: BASIC_TYPE.Number,
        allowEmpty: false
      },
      userId: {
        required: true,
        type: BASIC_TYPE.String,
        allowEmpty: false
      },
      userSig: {
        required: true,
        type: BASIC_TYPE.String,
        allowEmpty: false
      },
      scene: {
        type: BASIC_TYPE.String,
        values: [Scene.LIVE, Scene.RTC]
      },
      role: {
        type: BASIC_TYPE.String,
        values: [UserRole.AUDIENCE, UserRole.ANCHOR]
      },
      roomId: {
        type: [BASIC_TYPE.String, BASIC_TYPE.Number]
      },
      strRoomId: {
        type: BASIC_TYPE.String
      },
      proxy: {
        type: [BASIC_TYPE.Object, BASIC_TYPE.String],
        properties: {
          websocketProxy: { type: BASIC_TYPE.String },
          turnServer: {
            type: [BASIC_TYPE.Object, BASIC_TYPE.Array],
            properties: {
              url: { required: true, type: BASIC_TYPE.String },
              username: { type: BASIC_TYPE.String },
              credential: { type: BASIC_TYPE.String },
              credentialType: { type: BASIC_TYPE.String, values: ['password'] }
            }
          },
          loggerProxy: { type: BASIC_TYPE.String },
          webtransportProxy: { type: BASIC_TYPE.String }
        }
      },
      // autoSubscribe: { type: BASIC_TYPE.Boolean },
      enableAutoPlayDialog: { type: BASIC_TYPE.Boolean },
      userDefineRecordId: { type: BASIC_TYPE.String },
      latencyLevel: {
        type: BASIC_TYPE.Number
      },
      playoutDelay: {
        type: BASIC_TYPE.Object,
        properties: {
          min: { type: BASIC_TYPE.Number, min: 0, max: 1000 },
          max: { type: BASIC_TYPE.Number, min: 0, max: 10000 }
        }
      },
      quickStart: {
        type: BASIC_TYPE.Object,
        properties: {
          remoteUserId: {
            type: BASIC_TYPE.String,
            required: true,
            allowEmpty: false
          },
          small: { type: BASIC_TYPE.Boolean },
          domain: { type: BASIC_TYPE.String }
        }
      }
      // pureAudioPushMode: {
      //   type: BASIC_TYPE.Number,
      //   values: [1, 2]
      // },
      // enableSEI: { type: BASIC_TYPE.Boolean }
    }
  },
  startLocalVideo: {
    name: 'LocalVideoConfig',
    type: BASIC_TYPE.Object,
    properties: {
      view: ViewValidateConfig,
      mute: {
        type: [BASIC_TYPE.Boolean, BASIC_TYPE.String]
      },
      publish: {
        type: BASIC_TYPE.Boolean
      },
      capture: {
        required: false,
        type: BASIC_TYPE.Boolean
      },
      option: LocalVideoConfigOption
    },
    validate(value: LocalVideoConfig) {
      if (!value?.option?.videoTrack && isUsedInHttpProtocol()) {
        throw new RtcError({
          code: ErrorCode.ENV_NOT_SUPPORTED,
          extraCode: ErrorCodeDictionary.NOT_SUPPORTED_HTTP
        });
      }
      if (value?.option?.small) {
        validateSmall(value);
      }
    }
  },
  updateLocalVideo: {
    name: 'updateLocalVideoConfig',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      view: { ...ViewValidateConfig, required: false },
      publish: {
        type: BASIC_TYPE.Boolean
      },
      capture: {
        required: false,
        type: BASIC_TYPE.Boolean
      },
      mute: {
        type: [BASIC_TYPE.Boolean, BASIC_TYPE.String]
      },
      option: LocalVideoConfigOption
    },
    validate(value: LocalVideoConfig) {
      if (value?.option?.small) {
        validateSmall(value);
      }
    }
  },
  startLocalAudio: {
    name: 'LocalAudioConfig',
    type: BASIC_TYPE.Object,
    properties: {
      publish: {
        type: BASIC_TYPE.Boolean
      },
      mute: {
        type: [BASIC_TYPE.Boolean, BASIC_TYPE.String],
        values: [true, false, 'microphone']
      },
      muteKeepVolumeDetection: {
        type: BASIC_TYPE.Boolean
      },
      option: LocalAudioConfigOption
    },
    validate(value: LocalAudioConfig) {
      if (!value?.option?.audioTrack && isUsedInHttpProtocol()) {
        throw new RtcError({
          code: ErrorCode.ENV_NOT_SUPPORTED,
          extraCode: ErrorCodeDictionary.NOT_SUPPORTED_HTTP
        });
      }
    }
  },
  updateLocalAudio: {
    name: 'updateLocalAudioConfig',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      publish: {
        type: BASIC_TYPE.Boolean
      },
      mute: {
        type: [BASIC_TYPE.Boolean, BASIC_TYPE.String],
        values: [true, false, 'microphone']
      },
      muteKeepVolumeDetection: {
        type: BASIC_TYPE.Boolean
      },
      option: LocalAudioConfigOption
    }
  },
  startScreenShare: {
    name: 'ScreenShareConfig',
    type: BASIC_TYPE.Object,
    properties: {
      view: ViewValidateConfig,
      publish: {
        type: BASIC_TYPE.Boolean
      },
      option: ScreenShareConfigOption
    },
    validate(value: ScreenShareConfig, key, fnName, className, target) {
      if (!value?.option?.videoTrack) {
        if (isUsedInHttpProtocol()) throw new RtcError({
          code: ErrorCode.ENV_NOT_SUPPORTED,
          extraCode: ErrorCodeDictionary.NOT_SUPPORTED_HTTP
        });
        if (!isScreenCaptureApiAvailable()) throw new RtcError({
          code: ErrorCode.ENV_NOT_SUPPORTED,
          fnName,
          extraCode: ErrorCodeDictionary.NOT_SUPPORTED_SCREEN_SHARE
        });
      }
    }
  },
  updateScreenShare: {
    name: 'updateScreenShareConfig',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      view: ViewValidateConfig,
      publish: {
        type: BASIC_TYPE.Boolean
      },
      option: ScreenShareConfigOption
    }
  },
  muteRemoteAudio: [UserIdValidateConfig, {
    name: 'mute',
    required: true,
    type: BASIC_TYPE.Boolean
  }],
  setRemoteAudioVolume: [UserIdValidateConfig, {
    name: 'volume',
    required: true,
    type: BASIC_TYPE.Number,
    min: 0
  }],
  startRemoteVideo: {
    name: 'startRemoteVideoConfig',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      view: ViewValidateConfig,
      userId: { type: BASIC_TYPE.String, required: true },
      streamType: { values: [TRTCStreamType.Main, TRTCStreamType.Sub], required: true },
      option: {
        type: BASIC_TYPE.Object,
        properties: {
          fillMode: { type: BASIC_TYPE.String, values: ['contain', 'cover', 'fill'] },
          mirror: { type: BASIC_TYPE.Boolean }
        }
      }
    },
    validate(this: TRTC, config: RemoteVideoConfig, key, fnName) {
      // 允许 whip 拉流的指定用户在 JOINING 阶段调用
      const isWhipTargetUser = this._room.whipQuickStartConfig?.remoteUserId === config.userId;
      validateJoinState(this._room.isJoined || isWhipTargetUser, fnName);
      const remotePublishedUser = this._room.remotePublishedUserMap.get(config.userId);
      validateRemoteUserId(!!remotePublishedUser, fnName, config);

      if (remotePublishedUser) {
        if ((config.streamType === TRTCStreamType.Main && !remotePublishedUser.muteState.videoAvailable) || (config.streamType === TRTCStreamType.Sub && !remotePublishedUser.muteState.hasAuxiliary)) {
          throw new RtcError({
            code: ErrorCode.INVALID_OPERATION,
            extraCode: ErrorCodeDictionary.INVALID_OPERATION_STREAM_TYPE_NOT_EXIST,
            fnName,
            messageParams: { value: config }
          });
        }
      }
    }
  },
  updateRemoteVideo: {
    name: 'updateRemoteVideoConfig',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      view: { ...ViewValidateConfig, required: false },
      userId: { type: BASIC_TYPE.String, required: true },
      streamType: { values: [TRTCStreamType.Main, TRTCStreamType.Sub], required: true },
      option: {
        type: BASIC_TYPE.Object,
        properties: {
          fillMode: { type: BASIC_TYPE.String, values: ['contain', 'cover', 'fill'] },
          mirror: { type: BASIC_TYPE.Boolean }
        }
      }
    },
    validate(this: TRTC, config: RemoteVideoConfig, key, fnName) {
      // 允许 whip 拉流的指定用户在 JOINING 阶段调用
      const isWhipTargetUser = this._room.whipQuickStartConfig?.remoteUserId === config.userId;
      validateJoinState(this._room.isJoined || isWhipTargetUser, fnName);
      const remotePublishedUser = this._room.remotePublishedUserMap.get(config.userId);
      validateRemoteUserId(!!remotePublishedUser, fnName, config);
      if (remotePublishedUser) {
        if ((config.streamType === TRTCStreamType.Main && !remotePublishedUser.muteState.videoAvailable) || (config.streamType === TRTCStreamType.Sub && !remotePublishedUser.muteState.hasAuxiliary)) {
          throw new RtcError({
            code: ErrorCode.INVALID_OPERATION,
            extraCode: ErrorCodeDictionary.INVALID_OPERATION_STREAM_TYPE_NOT_EXIST,
            fnName,
            messageParams: { value: config }
          });
        }
        if (config.option) {
          const remoteTrack = config.streamType === TRTCStreamType.Main ? remotePublishedUser.remoteVideoTrack : remotePublishedUser.remoteAuxiliaryTrack;
          if (((config.option.pictureInPicture || config.option.fullScreen) || config.option.fullScreen) && (!remoteTrack.isSubscribed || !remoteTrack.player.isPlaying)) {
            throw new RtcError({
              code: ErrorCode.INVALID_OPERATION,
              message: 'cannot set pictureInPicture or fullScreen when remote video is not playing'
            });
          }
        }
      }
    }
  },
  stopRemoteVideo: {
    name: 'stopRemoteVideoConfig',
    type: BASIC_TYPE.Object,
    required: true,
    properties: {
      userId: { type: BASIC_TYPE.String, required: true },
      streamType: { values: [TRTCStreamType.Main, TRTCStreamType.Sub] }
    },
    validate(config, key: string, fnName: string) {
      if (config.userId !== '*' && isUndefined(config.streamType)) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          extraCode: ErrorCodeDictionary.INVALID_STREAM_TYPE,
          fnName
        });
      }
    }
  },
  switchRole: {
    name: 'role',
    required: true,
    values: [UserRole.ANCHOR, UserRole.AUDIENCE],
    validate(this: { _room: IRoom; }, value, key, fnName) {
      validateJoinState(this._room.isJoining || this._room.isJoined, fnName);
      // if (!this._room.isJoined) {
      //   throw new RtcError({
      //     code: ErrorCode.INVALID_OPERATION,
      //     message: generateErrorMessage({ key: ErrorName.INVALID_OPERATION_SWITCH_ROLE })
      //   });
      // }
      // if (this._room.mode !== Scene.LIVE) {
      //   // role is only valid in live mode.
      //   throw new RtcError({
      //     code: ErrorCode.INVALID_PARAMETER,
      //     message: generateErrorMessage({ key: ErrorName.INVALID_ROLE })
      //   });
      // }
    }
  },
  enableAudioVolumeEvaluation: [
    {
      name: 'interval',
      type: BASIC_TYPE.Number
    },
    {
      name: 'enableInBackground',
      type: BASIC_TYPE.Boolean
    }
  ],
  sendSEIMessage: [
    {
      name: 'buffer',
      required: true,
      instanceOf: ArrayBuffer,
      validate(this: TRTC, buffer: ArrayBuffer, key: string, fnName: string, className: string) {
        if (!IS_SEI_SUPPORTED) {
          throw new RtcError({
            code: ErrorCode.ENV_NOT_SUPPORTED,
            fnName,
            extraCode: ErrorCodeDictionary.NOT_SUPPORTED_SEI
          });
        }
        if (!this._room.enableSEI) {
          throw new RtcError({
            code: ErrorCode.INVALID_OPERATION,
            fnName,
            extraCode: ErrorCodeDictionary.INVALID_NOT_ENABLE_SEI
          });
        }
        if (buffer.byteLength > 1000) {
          throw new RtcError({
            code: ErrorCode.INVALID_PARAMETER,
            extraCode: ErrorCodeDictionary.INVALID_BUFFER_OVERSIZE,
            fnName
          });
        }

        if (buffer.byteLength === 0) {
          throw new RtcError({
            code: ErrorCode.INVALID_PARAMETER,
            extraCode: ErrorCodeDictionary.INVALID_BUFFER_EMPTY,
            messageParams: { key },
            fnName
          });
        }
        validateJoinState(this._room.isJoined, fnName);
      }
    },
    {
      name: 'options',
      type: BASIC_TYPE.Object,
      properties: {
        seiPayloadType: {
          type: BASIC_TYPE.Number,
          values: [5, 243]
        },
        toSubStream: {
          type: BASIC_TYPE.Boolean,
          validate(this: TRTC, toSubStream: boolean, key: string, fnName: string) {
            if ((!toSubStream && !this._room.isMainStreamPublished) || (toSubStream && !this._room.isAuxStreamPublished)) {
              throw new RtcError({
                code: ErrorCode.INVALID_OPERATION,
                extraCode: ErrorCodeDictionary.INVALID_NEED_CALL_PUBLISHED,
                messageParams: ({ key }),
                fnName
              });
            }
          }
        }
      }
    }
  ],
  sendCustomMessage: {
    name: 'message',
    required: true,
    type: BASIC_TYPE.Object,
    properties: {
      cmdId: {
        type: BASIC_TYPE.Number,
        required: true,
        min: 1,
        max: 10
      },
      data: {
        instanceOf: ArrayBuffer,
        required: true,
        validate(this: TRTC, data: ArrayBuffer, key: string, fnName: string, className: string) {
          if (data.byteLength > 1000) {
            throw new RtcError({
              code: ErrorCode.INVALID_PARAMETER,
              extraCode: ErrorCodeDictionary.INVALID_BUFFER_OVERSIZE,
              fnName
            });
          }

          if (data.byteLength === 0) {
            throw new RtcError({
              code: ErrorCode.INVALID_PARAMETER,
              extraCode: ErrorCodeDictionary.INVALID_BUFFER_EMPTY,
              fnName,
              messageParams: { key }
            });
          }
        }
      }
    },
    validate(this: TRTC, value, key, fnName) {
      validateJoinState(this._room.isJoined, fnName);
      if (this._room.scene === Scene.LIVE && this._room.role === UserRole.AUDIENCE) {
        throw new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          extraCode: ErrorCodeDictionary.INVALID_ROLE_AUDIENCE,
          fnName,
          messageParams: { key }
        });
      }
    }
  },
  switchRoom: {
    name: 'switchRoomConfig',
    type: BASIC_TYPE.Object,
    required: true,
    validate(this: { _room: IRoom; }, value: SwitchRoomConfig, key: string, fnName: string) {
      validateJoinState(this._room.isJoined, fnName);
      // 切换的房间号不可以和旧房间号相同
      if ((this._room.useStringRoomId && value.strRoomId === this._room.roomId) || (!this._room.useStringRoomId && value.roomId === Number(this._room.roomId))) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_DUPLICATE,
          fnName,
          messageParams: { key: this._room.useStringRoomId ? 'strRoomId' : 'roomId' }
        });
      }
      // 1. 如果两个类型的房间号都填则优先使用数字类型的房间号 2. 切换房间的房间号类型需要和旧房间的一致
      if ((value.roomId && this._room.useStringRoomId) || (!value.roomId && value.strRoomId && !this._room.useStringRoomId)) {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_TYPE_MISMATCH,
          fnName,
          messageParams: { key: this._room.useStringRoomId ? 'strRoomId' : 'roomId' }
        });
      }
      if (value.roomId) {
        validateRoomId(value.roomId, fnName, key);
      } else if (value.strRoomId) {
        validateStrRoomId(value.strRoomId, fnName, key);
      } else {
        throw new RtcError({
          code: ErrorCode.INVALID_PARAMETER,
          extraCode: ErrorCodeDictionary.INVALID_ROOM_ID_REQUIRED,
          fnName
        });
      }
    },
    properties: {
      roomId: {
        type: BASIC_TYPE.Number
      },
      strRoomId: {
        type: BASIC_TYPE.String
      },
      privateMapKey: { type: BASIC_TYPE.String },
      userSig: {
        type: BASIC_TYPE.String,
        required: true
      },
      autoSubscribeCount: { type: BASIC_TYPE.Number, min: 0, max: 50 }
    }
  }
} as const);

export default { TRTC: TRTCValidateConfig };
